import type { Team } from '@nexo/types';
import { Avatar, cn, useContextMenu, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  BadgeCheck,
  Compass,
  ExternalLink,
  House,
  Link2,
  LogOut,
  Plus,
  Settings,
  Shield,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useNavigate, useParams } from 'react-router';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/forms';
import { keys, useFeatures, useTeams } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { CreateTeamDialog } from './CreateTeamDialog';
import { TeamSettingsDialog } from './TeamSettingsDialog';

/** Narrow vertical bar to switch teams, like workspaces. Right-click a team for its actions. */
export function TeamRail() {
  const { teamId } = useParams();
  const { data: teams = [] } = useTeams();
  const features = useFeatures();
  const [creating, setCreating] = useState(false);
  const [settingsFor, setSettingsFor] = useState<Team | null>(null);
  const me = useMe();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const menu = useContextMenu();

  const leave = useMutation({
    mutationFn: (team: Team) => api.teams.leave(team.id),
    onSuccess: (_, team) => {
      queryClient.setQueryData<Team[]>(keys.teams, (list) =>
        list?.filter((other) => other.id !== team.id),
      );
      if (team.id === teamId) void navigate('/app');
    },
    onError: (err) =>
      toast({ title: t('No puedes salir'), description: errorMessage(err), tone: 'error' }),
  });

  // The person's own order: drag a team (or use «Mover arriba/abajo»), saved for all devices.
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.teams.reorder(ids),
    onMutate: (ids) => {
      const previous = queryClient.getQueryData<Team[]>(keys.teams);
      queryClient.setQueryData<Team[]>(keys.teams, (list) =>
        list ? ids.flatMap((id) => list.filter((team) => team.id === id)) : list,
      );
      return { previous };
    },
    onSuccess: (list) => queryClient.setQueryData(keys.teams, list),
    onError: (err, _, context) => {
      queryClient.setQueryData(keys.teams, context?.previous);
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' });
    },
  });
  const move = (teamId: string, to: number) => {
    const ids = teams.map((team) => team.id).filter((id) => id !== teamId);
    ids.splice(Math.max(0, Math.min(to, ids.length)), 0, teamId);
    if (ids.join() !== teams.map((team) => team.id).join()) reorder.mutate(ids);
  };
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);

  const entriesFor = (team: Team, index: number) => [
    {
      label: t('Abrir'),
      icon: <ExternalLink />,
      onSelect: () => void navigate(`/app/t/${team.id}`),
    },
    {
      label: t('Ajustes del equipo'),
      icon: <SlidersHorizontal />,
      onSelect: () => setSettingsFor(team),
    },
    index > 0 && {
      label: t('Mover arriba'),
      icon: <ArrowUp />,
      onSelect: () => move(team.id, index - 1),
    },
    index < teams.length - 1 && {
      label: t('Mover abajo'),
      icon: <ArrowDown />,
      onSelect: () => move(team.id, index + 1),
    },
    {
      label: t('Copiar enlace'),
      icon: <Link2 />,
      onSelect: () => {
        void navigator.clipboard.writeText(`${window.location.origin}/app/t/${team.id}`);
        toast({ title: t('Enlace copiado'), tone: 'success' });
      },
    },
    'separator' as const,
    team.role === 'owner'
      ? {
          label: t('Eliminar equipo'),
          icon: <Trash2 />,
          danger: true,
          onSelect: () => setSettingsFor(team),
        }
      : {
          label: t('Salir del equipo'),
          icon: <LogOut />,
          danger: true,
          onSelect: () => leave.mutate(team),
        },
  ];

  return (
    <nav
      aria-label={t('Equipos')}
      className="flex w-[60px] shrink-0 flex-col items-center gap-2 border-r border-border pt-2 pb-3 md:border-r-0"
    >
      <div className="scrollbar-thin flex w-full flex-1 flex-col items-center gap-3 overflow-y-auto px-2 pt-1.5 pb-2">
        {teams.map((team, index) => {
          const active = team.id === teamId;
          return (
            <Link
              key={team.id}
              to={`/app/t/${team.id}`}
              title={team.official ? `${team.name} · ${t('Servidor oficial de Nexo')}` : team.name}
              aria-current={active ? 'page' : undefined}
              onContextMenu={menu.open(() => entriesFor(team, index))}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', team.id);
                setDragging(team.id);
              }}
              onDragOver={(e) => {
                if (!dragging) return;
                e.preventDefault();
                const box = e.currentTarget.getBoundingClientRect();
                setDropAt(e.clientY < box.top + box.height / 2 ? index : index + 1);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging && dropAt !== null) {
                  const from = teams.findIndex((other) => other.id === dragging);
                  move(dragging, dropAt > from ? dropAt - 1 : dropAt);
                }
                setDragging(null);
                setDropAt(null);
              }}
              onDragEnd={() => {
                setDragging(null);
                setDropAt(null);
              }}
              className={cn(
                'group relative flex items-center',
                dragging === team.id && 'opacity-40',
              )}
            >
              {dragging && dropAt === index && (
                <span className="absolute -top-2 -right-1 -left-1 h-0.5 rounded-full bg-accent" />
              )}
              {dragging && dropAt === index + 1 && index === teams.length - 1 && (
                <span className="absolute -right-1 -bottom-2 -left-1 h-0.5 rounded-full bg-accent" />
              )}
              <span
                className={cn(
                  'absolute -left-[12px] w-[3px] rounded-r-full bg-text transition-all',
                  active ? 'h-8' : 'h-0 group-hover:h-4',
                )}
              />
              <Avatar
                id={team.id}
                name={team.name}
                shape="square"
                className={cn(
                  'rounded-lg transition',
                  active
                    ? 'ring-2 ring-text ring-offset-2 ring-offset-canvas'
                    : 'opacity-70 group-hover:opacity-100',
                )}
              />
              {team.official && (
                <BadgeCheck className="absolute -right-1.5 -bottom-1.5 size-4 fill-accent text-canvas" />
              )}
            </Link>
          );
        })}
        <button
          type="button"
          title={t('Crear equipo')}
          aria-label={t('Crear equipo')}
          onClick={() => setCreating(true)}
          className="flex size-9 items-center justify-center rounded-lg border border-dashed border-border-strong text-subtle transition-colors hover:border-text hover:text-text"
        >
          <Plus className="size-4" />
        </button>
        {features.data?.publicDirectory && (
          <RailLink to="/app/explore" label={t('Explorar servidores')} icon={<Compass />} />
        )}
      </div>
      {me.isPlatformAdmin && <RailLink to="/admin" label={t('Administración')} icon={<Shield />} />}
      <RailLink to="/app/home" label={t('Todos los equipos')} icon={<House />} />
      <RailLink to="/app/profile" label={t('Perfil y ajustes')} icon={<Settings />} />
      <CreateTeamDialog open={creating} onClose={() => setCreating(false)} />
      {settingsFor && (
        <TeamSettingsDialog
          // The dialog edits the latest copy of the team.
          team={teams.find((team) => team.id === settingsFor.id) ?? settingsFor}
          open
          onClose={() => setSettingsFor(null)}
        />
      )}
      {menu.element}
    </nav>
  );
}

function RailLink({ to, label, icon }: { to: string; label: string; icon: ReactNode }) {
  return (
    <NavLink
      to={to}
      title={label}
      aria-label={label}
      className="flex size-9 shrink-0 items-center justify-center rounded-lg text-subtle hover:bg-surface hover:text-text [&_svg]:size-[18px]"
    >
      {icon}
    </NavLink>
  );
}
